// components/StudentPortal/Home/StudentHome.js
// Home "Hoy" (S1, F4 + refinamiento). Orientado a accion: saludo -> hero (lo que
// toca ahora, NUNCA una tarea al 100% como "en curso") -> mis tareas (cards con
// estado/CTA/anillo) -> mi clase (tutora + banner de escena). Tokenizado.
import React from "react";
import { useTranslation } from "react-i18next";
import { useStudentHome } from "./useStudentHome";
import { StudentAssistant } from "../Assistant/StudentAssistant";
import noAvatar from "../../../assets/png/Educa360Logo.png";
import styles from "./StudentHome.module.scss";

// Estado de presentacion -> {label, pill class}
function statusMeta(display, t) {
  switch (display) {
    case "completed":
      return { label: t("newHome.statusCompleted"), cls: "pillGreen" };
    case "exam_pending":
      return { label: t("newHome.statusExamPending"), cls: "pillAmber" };
    case "in_progress":
      return { label: t("newHome.statusInProgress"), cls: "pillGreen" };
    case "not_started":
    default:
      return { label: t("newHome.statusNotStarted"), cls: "pillGray" };
  }
}

// CTA por estado. Si la tarea completada llevaba examen, se puede REPETIR.
function ctaLabel(display, examRequired, t) {
  switch (display) {
    case "completed":
      return examRequired ? t("newHome.examRetry") : t("newHome.review");
    case "exam_pending":
      return t("newHome.doExam");
    case "in_progress":
      return t("newHome.continueTask");
    case "not_started":
    default:
      return t("newHome.start");
  }
}

// Anillo de progreso (conic-gradient, sin SVG).
function Ring({ value }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div
      className={styles.ring}
      style={{
        background: `conic-gradient(var(--edu-color-primary, #00A75A) ${v * 3.6}deg, #eef0f4 0)`,
      }}
    >
      <span>{v}%</span>
    </div>
  );
}

export function StudentHome({ onOpenTask }) {
  const { t } = useTranslation("students");
  const {
    loading,
    error,
    student,
    tasks,
    tutor,
    assistant,
    activeScene,
    featuredTask,
    reload,
    trackSceneOpen,
  } = useStudentHome();

  const openTask = (id) => {
    if (id && typeof onOpenTask === "function") onOpenTask(id);
  };

  if (loading)
    return <div className={styles.state}>{t("newHome.loading")}</div>;
  if (error === "no-student")
    return <div className={styles.state}>{t("newHome.noStudent")}</div>;
  if (error)
    return (
      <div className={styles.state}>
        {t("newHome.error")}{" "}
        <button type="button" className={styles.linkBtn} onClick={reload}>
          {t("newHome.retry")}
        </button>
      </div>
    );

  const name = student?.name || "";
  const classnumber = student?.classnumber || "";

  const openScene = () => {
    if (activeScene?.enterUrl) {
      trackSceneOpen();
      window.open(activeScene.enterUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={styles.home}>
      {/* Saludo */}
      <header className={styles.greeting}>
        <img
          className={styles.avatar}
          src={student?.avatar || noAvatar}
          alt=""
        />
        <div>
          <h1 className={styles.hello}>{t("newHome.greeting", { name })}</h1>
          <p className={styles.sub}>
            {classnumber}
            {tutor?.name ? ` · ${t("newHome.tutor")}: ${tutor.name}` : ""}
          </p>
        </div>
      </header>

      {/* BLOQUE PRINCIPAL: la SALA ACTIVA manda (la abre el profe en el
          itinerario). Si no hay sala, sube la tarea destacada; si no, vacio. */}
      <section className={styles.hero}>
        {activeScene ? (
          <div className={styles.heroCard}>
            {activeScene.banner ? (
              <div
                className={styles.heroBanner}
                style={{ backgroundImage: `url(${activeScene.banner})` }}
              />
            ) : null}
            <div className={styles.heroBody}>
              <span className={`${styles.pill} ${styles.pillGreen}`}>
                {t("newHome.activeScene")}
              </span>
              <h2>{activeScene.title}</h2>
              {activeScene.category ? (
                <p className={styles.heroMeta}>{activeScene.category}</p>
              ) : null}
              <button
                type="button"
                className={styles.ctaPrimary}
                onClick={openScene}
              >
                {t("newHome.enterScene")}
              </button>
            </div>
          </div>
        ) : featuredTask ? (
          <div className={styles.heroCard}>
            <div className={styles.heroBody}>
              <span className={styles.ribbon}>{t("newHome.now")}</span>
              <span
                className={`${styles.pill} ${
                  featuredTask.kind === "examPending"
                    ? styles.pillAmber
                    : styles.pillGreen
                }`}
              >
                {featuredTask.kind === "examPending"
                  ? t("newHome.statusExamPending")
                  : t("newHome.taskInProgress")}
              </span>
              <h2>{featuredTask.row.task?.title}</h2>
              <p className={styles.heroMeta}>
                {featuredTask.kind === "examPending"
                  ? t("newHome.examReady")
                  : t("newHome.progress", { pct: featuredTask.row._progress })}
              </p>
              <button
                type="button"
                className={styles.ctaPrimary}
                onClick={() => openTask(featuredTask.row.task?._id)}
              >
                {featuredTask.kind === "examPending"
                  ? t("newHome.doExam")
                  : featuredTask.cta === "start"
                    ? t("newHome.start")
                    : t("newHome.continueTask")}
              </button>
            </div>
          </div>
        ) : (
          <div className={`${styles.heroCard} ${styles.heroEmpty}`}>
            <div className={styles.heroBody}>
              <h2>{t("newHome.emptyTitle")}</h2>
              <p className={styles.heroMeta}>{t("newHome.emptyText")}</p>
              <button
                type="button"
                className={styles.ctaGhost}
                onClick={reload}
              >
                {t("newHome.refresh")}
              </button>
            </div>
          </div>
        )}
      </section>

      <div className={styles.columns}>
        {/* Mis tareas */}
        <section className={styles.col}>
          <h3 className={styles.colTitle}>{t("newHome.myTasks")}</h3>
          {tasks.length === 0 ? (
            <p className={styles.muted}>{t("newHome.noTasks")}</p>
          ) : (
            <div className={styles.taskGrid}>
              {tasks.map((row) => {
                const task = row.task || {};
                const sm = statusMeta(row._display, t);
                return (
                  <div className={styles.taskCard} key={task._id || row._id}>
                    <div className={styles.taskHead}>
                      <span className={styles.taskName}>{task.title}</span>
                      <span className={`${styles.pill} ${styles[sm.cls]}`}>
                        {sm.label}
                      </span>
                    </div>
                    <div className={styles.taskBody}>
                      <Ring value={row._progress} />
                      <div className={styles.taskMeta}>
                        {row._examRequired ? (
                          <span className={styles.muted}>
                            {t("newHome.examLabel")}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className={
                            row._display === "in_progress" ||
                            row._display === "exam_pending"
                              ? styles.ctaPrimarySm
                              : styles.ctaGhostSm
                          }
                          onClick={() => openTask(task._id)}
                        >
                          {ctaLabel(row._display, row._examRequired, t)}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Mi clase = la profesora + asistente. La sala activa vive en el bloque
            principal de arriba, no se duplica aqui. */}
        <aside className={styles.side}>
          <h3 className={styles.colTitle}>{t("newHome.myClass")}</h3>
          <div className={styles.classCard}>
            {tutor ? (
              <div className={styles.tutor}>
                <img
                  className={styles.tutorAvatar}
                  src={tutor.avatar || noAvatar}
                  alt=""
                />
                <div>
                  <p className={styles.tutorName}>{tutor.name}</p>
                  <p className={styles.muted}>{classnumber}</p>
                </div>
              </div>
            ) : (
              <p className={styles.className}>{classnumber || "—"}</p>
            )}
          </div>

          {/* Asistente virtual (real, no "EMMA"). Card + burbuja flotante + chat. */}
          <StudentAssistant
            assistant={assistant}
            label={
              !activeScene && !featuredTask
                ? t("newHome.assistantWaiting")
                : t("newHome.assistant")
            }
          />
        </aside>
      </div>
    </div>
  );
}
